import { useEffect, useState } from "react";
import axios from "axios";
import { API } from "../App";

// Module-level cache so multiple consumers on the same page only trigger one fetch.
// Refresh happens on page navigation / reload, which is appropriate for marketing copy.
let cachedStats = null;
let inflight = null;

async function fetchStats() {
  if (cachedStats) return cachedStats;
  if (inflight) return inflight;
  inflight = axios
    .get(`${API}/grants/public-stats`)
    .then((res) => {
      cachedStats = res.data;
      inflight = null;
      return cachedStats;
    })
    .catch(() => {
      inflight = null;
      return null;
    });
  return inflight;
}

/**
 * usePublicStats — fetches public grant stats once per page load.
 * Returns { total_active, closing_next_14_days, corporate_scouts } or null while loading.
 */
export function usePublicStats() {
  const [stats, setStats] = useState(cachedStats);
  useEffect(() => {
    let alive = true;
    fetchStats().then((data) => {
      if (alive) setStats(data);
    });
    return () => { alive = false; };
  }, []);
  return stats;
}

/**
 * <DynamicGrantCount /> — inline component that renders the current active-grant count.
 *
 * Props:
 *   fallback — string to render before stats load (default: "100")
 *   plus — boolean, whether to append "+" (default: false; honest count)
 *
 * Use inline:  Browse all <DynamicGrantCount /> SC grants
 */
export default function DynamicGrantCount({ fallback = "100", plus = false }) {
  const stats = usePublicStats();
  const n = stats?.total_active;
  if (!n) return <span data-testid="dynamic-grant-count">{fallback}</span>;
  return <span data-testid="dynamic-grant-count">{n}{plus ? "+" : ""}</span>;
}

/**
 * <DynamicScoutCount /> — inline component that renders the current verified
 * corporate-funder ("Corporate Scout") count.
 *
 * Props:
 *   fallback — string to render before stats load (default: "30")
 *   plus — boolean, whether to append "+" (default: false; honest count)
 */
export function DynamicScoutCount({ fallback = "30", plus = false }) {
  const stats = usePublicStats();
  const n = stats?.corporate_scouts;
  if (!n) return <span data-testid="dynamic-scout-count">{fallback}</span>;
  return <span data-testid="dynamic-scout-count">{n}{plus ? "+" : ""}</span>;
}
